<template>
  <v-form ref="form" @submit.prevent>
    <v-row dense>
      <!-- Change History (whole record) -->
      <v-col v-if="isEdit && auditRecordId" cols="12" class="d-flex justify-end">
        <audit-history-button
          entity-type="Task"
          :record-id="auditRecordId"
          title="Task Change History"
        />
      </v-col>

      <!-- Title -->
      <v-col cols="12">
        <v-text-field
          label="Title"
          :model-value="localTask.title"
          :rules="[rules.required]"
          placeholder="Enter task title..."
          required
          variant="outlined"
          density="comfortable"
          @update:model-value="onField('title', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="Title"
              field-label="Title"
            />
          </template>
        </v-text-field>
      </v-col>

      <!-- Description -->
      <v-col cols="12">
        <v-textarea
          label="Description"
          :model-value="localTask.description"
          :rules="[rules.required]"
          placeholder="Enter task description..."
          auto-grow
          required
          variant="outlined"
          density="comfortable"
          rows="3"
          @update:model-value="onField('description', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="Description"
              field-label="Description"
            />
          </template>
        </v-textarea>
      </v-col>

      <!-- Priority -->
      <v-col cols="12" md="6">
        <v-select
          label="Priority"
          :model-value="localTask.priority"
          :items="priorityOptions"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
          required
          variant="outlined"
          density="comfortable"
          @update:model-value="onField('priority', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="PriorityFK"
              field-label="Priority"
            />
          </template>
        </v-select>
      </v-col>

      <!-- Status -->
      <v-col cols="12" md="6">
        <v-select
          label="Status"
          :model-value="localTask.status"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          :rules="[rules.required]"
          required
          variant="outlined"
          density="comfortable"
          @update:model-value="onField('status', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="StatusFK"
              field-label="Status"
            />
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" md="6">
        <v-select
          label="Assign To"
          :model-value="localTask.assignedTo"
          :items="users"
          item-title="userName"
          item-value="id"
          :rules="[rules.required]"
          placeholder="Select User"
          required
          variant="outlined"
          density="comfortable"
          @update:model-value="onField('assignedTo', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="AssignedToFK"
              field-label="Assigned To"
            />
          </template>
          <template #prepend-item>
            <v-list-item title="Select assignee" />
            <v-divider />
          </template>
          <template #item="{ item, props }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-avatar size="28" class="mr-2">
                  <v-img :src="getUserIconUrl(item.raw.iconId)" />
                </v-avatar>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <!-- Assignment email template: only offer a choice when more than one
           template exists (the default is preselected and used otherwise). -->
      <v-col v-if="localTask.assignedTo && assignmentTemplates.length > 1" cols="12">
        <v-select
          label="Assignment Email Template"
          :model-value="localTask.emailTemplateId"
          :items="assignmentTemplates"
          item-title="name"
          item-value="templateId"
          :loading="loadingTemplates"
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-email-outline"
          hint="Which email is sent to the assignee"
          persistent-hint
          @update:model-value="onField('emailTemplateId', $event)"
        >
          <template #item="{ item, props }">
            <v-list-item v-bind="props">
              <template #append>
                <v-chip v-if="item.raw.isDefault" size="x-small" color="success" variant="flat">
                  Default
                </v-chip>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <!-- Due Date & Estimated Cost Row -->
      <v-col cols="12" md="6">
        <v-text-field
          label="Due Date"
          type="date"
          :model-value="dueDateInput"
          :rules="[rules.required]"
          required
          variant="outlined"
          density="comfortable"
          prepend-inner-icon="mdi-calendar"
          @update:model-value="dueDateInput = $event"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="DueDate"
              field-label="Due Date"
            />
          </template>
        </v-text-field>
      </v-col>

      <v-col cols="12" md="6">
        <v-text-field
          label="Estimated Cost"
          type="number"
          prefix="€"
          placeholder="0.00"
          :model-value="localTask.estimatedCost"
          variant="outlined"
          density="comfortable"
          @update:model-value="onField('estimatedCost', $event)"
        >
          <template #append>
            <audit-field-timeline
              v-if="isEdit && auditRecordId"
              entity-type="Task"
              :record-id="auditRecordId"
              field-key="EstimatedCost"
              field-label="Estimated Cost"
            />
          </template>
        </v-text-field>
      </v-col>

      <!-- Location: capture GPS or display -->
      <v-col cols="12">
        <v-divider class="my-2" />
        <div class="v-label mb-2">
          <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
          Location
        </div>
        <v-card variant="outlined" class="pa-3" color="grey-lighten-5">
          <div class="d-flex align-center flex-wrap ga-2">
            <CoordinateDisplay
              :lat="localTask.latitude"
              :lon="localTask.longitude"
              layout="inline"
            />
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              :loading="capturingLocation"
              @click="captureLocation"
            >
              <v-icon start size="18">mdi-crosshairs-gps</v-icon>
              Capture GPS
            </v-btn>
          </div>
        </v-card>
      </v-col>

      <!-- Photos (create & edit mode) -->
      <v-col cols="12">
        <v-divider class="my-2" />
        <div class="v-label mb-2">
          <v-icon size="16" class="mr-1">mdi-image-multiple</v-icon>
          Task Photo(s) (optional)
        </div>
        <v-file-input
          :model-value="newPhotoFiles"
          @update:model-value="onNewPhotosChange"
          accept="image/*"
          multiple
          show-size
          prepend-icon="mdi-image-plus"
          density="comfortable"
          clearable
          variant="outlined"
          label="Add photos"
        />
      </v-col>
    </v-row>

    <!-- Action Buttons -->
    <v-row class="mt-4">
      <v-col cols="12" class="d-flex justify-end flex-wrap" style="gap: 8px">
        <v-btn variant="outlined" @click="$emit('cancel')">
          <v-icon start>mdi-close</v-icon>
          Cancel
        </v-btn>

        <v-btn
          v-if="isEdit"
          color="error"
          variant="flat"
          @click="$emit('delete', localTask)"
        >
          <v-icon start>mdi-trash-can</v-icon>
          Delete
        </v-btn>

        <v-btn
          v-if="isEdit && resendTaskId && localTask.assignedTo"
          color="primary"
          variant="tonal"
          :loading="resending"
          :disabled="resending || saving"
          type="button"
          @click="onResendEmail"
        >
          <v-icon start>mdi-email-send</v-icon>
          Resend Email
        </v-btn>

        <v-btn
          color="green"
          variant="flat"
          :loading="saving"
          :disabled="saving"
          type="button"
          @click="onSave"
        >
          <v-icon start>mdi-check</v-icon>
          {{ isEdit ? "Save Changes" : "Create Task" }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import CoordinateDisplay from "@/components/CoordinateDisplay.vue";
import AuditFieldTimeline from "@/components/audit/AuditFieldTimeline.vue";
import { useAuthStore } from "@/stores/auth";
import { createTask, updateTask, resendTaskAssignmentEmail } from "@/api/tasks";
import { getLookupDataByType } from "@/api/lookups";
import emailTemplateApi from "@/api/emailTemplates";
import { uploadPhoto, reconcilePhotoSnapshots, revokePhotoSnapshots, snapshotsToFiles } from "@/api/photoItems";

function getUserIconUrl(id) {
  const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
  return `/images/avatars/${key}.svg`;
}

export default {
  name: "TaskForm",
  components: { CoordinateDisplay, AuditFieldTimeline },
  props: {
    modelValue: { type: Object, default: () => ({}) }, // v-model (do NOT mutate directly)
    finding: { type: Object, default: null }, // read-only
    users: { type: Array, default: () => [] },
    isEdit: { type: Boolean, default: false },
    zoneId: { type: [Number, String], default: null },
  },
  emits: ["update:modelValue", "saved", "cancel", "delete"],
  data() {
    return {
      localTask: this.withDefaults(this.modelValue),
      saving: false,
      resending: false,
      prefilledOnce: false,
      priorityOptions: [],
      loadingPriorities: false,
      prioritiesLoaded: false,
      statusOptions: [],
      loadingStatuses: false,
      statusesLoaded: false,
      assignmentTemplates: [],
      loadingTemplates: false,
      templatesLoaded: false,
      capturingLocation: false,
      newPhotoSnapshots: [],
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== "") || "Required",
      },
    };
  },
  async mounted() {
    if (!this.prioritiesLoaded && !this.loadingPriorities) {
      await this.loadPriorityOptions();
    }
    if (!this.statusesLoaded && !this.loadingStatuses) {
      await this.loadStatusOptions();
    }
    await this.loadAssignmentTemplates();
  },
  beforeUnmount() {
    revokePhotoSnapshots(this.newPhotoSnapshots);
  },
  computed: {
    newPhotoFiles() {
      // Stable File[] derived from iOS-safe snapshots, bound into v-file-input.
      return snapshotsToFiles(this.newPhotoSnapshots);
    },
    resendTaskId() {
      return this.localTask?.taskId || this.localTask?.id || null;
    },
    // Record id used for the Change History button + per-field history icons.
    auditRecordId() {
      return this.localTask?.taskId || this.localTask?.id || null;
    },
    // Map yyyy-mm-dd ↔ internal value; emit on set
    dueDateInput: {
      get() {
        const v = this.localTask?.dueDate;
        if (!v) return null;
        const d = typeof v === "string" ? new Date(v) : v;
        return Number.isNaN(d?.getTime?.()) ? null : d.toISOString().slice(0, 10);
      },
      set(val) {
        this.onField("dueDate", val); // ensure we EMIT when date changes
      },
    },
  },
  watch: {
    // only react when parent replaces the object (no deep ping-pong)
    modelValue: {
      deep: false,
      immediate: true,
      handler(nv) {
        this.localTask = this.withDefaults(nv || {});
        // try prefill once when both finding and local are available
        this.tryPrefillFromFinding();
      },
    },
    // if finding arrives later, prefill only once
    finding: {
      deep: false,
      immediate: true,
      handler() {
        this.tryPrefillFromFinding();
      },
    },
  },
  methods: {
    async onNewPhotosChange(files) {
      // iOS-safe: snapshot bytes into memory immediately so iOS can't evict
      // the camera-temp file before the user hits Save.
      this.newPhotoSnapshots = await reconcilePhotoSnapshots(this.newPhotoSnapshots, files);
    },
    withDefaults(task) {
      const base = {
        id: null,
        title: "",
        description: "",
        zoneId: this.zoneId ?? null,
        findingId: this.finding?.findingId ?? null,
        assignedTo: null,
        dueDate: null, // keep as yyyy-mm-dd string
        estimatedCost: null,
        priority: null,
        emailTemplateId: null, // chosen TaskAssignment email template (null = default)
        status: this.isEdit ? undefined : null, // blank on create (user must choose, like Priority)
        latitude: this.finding?.latitude ?? null,
        longitude: this.finding?.longitude ?? null,
        accuracy: this.finding?.accuracy ?? null,
      };
      return { ...base, ...(task || {}) };
    },
    // prefill once, only for empty fields, only in create mode
    tryPrefillFromFinding() {
      if (this.prefilledOnce || !this.finding || this.isEdit) return;
      const t = { ...this.localTask };
      let touched = false;

      // Title and description are intentionally left blank when creating
      // a task from a finding — user fills them in themselves.

      if (!t.latitude && this.finding.latitude) {
        t.latitude = this.finding.latitude;
        touched = true;
      }
      if (!t.longitude && this.finding.longitude) {
        t.longitude = this.finding.longitude;
        touched = true;
      }
      if (!t.accuracy && this.finding.accuracy) {
        t.accuracy = this.finding.accuracy;
        touched = true;
      }
      if (!t.findingId && this.finding.findingId) {
        t.findingId = this.finding.findingId;
        touched = true;
      }
      if (!t.zoneId && this.finding.zoneId) {
        t.zoneId = this.finding.zoneId;
        touched = true;
      }

      if (touched) {
        this.localTask = t;
        this.emitUp();
      }
      this.prefilledOnce = true;
    },
    onField(key, val) {
      const next = { ...this.localTask };
      // number coercion for some fields
      if (key === "priority" || key === "estimatedCost" || key === "status") {
        next[key] = val === "" || val === null ? null : Number(val);
      } else {
        next[key] = val;
      }
      this.localTask = next;
      this.emitUp();
    },
    emitUp() {
      // emit a NEW object reference to avoid watcher equality edge cases
      this.$emit("update:modelValue", { ...this.localTask });
    },
    async loadStatusOptions() {
      if (this.loadingStatuses || this.statusesLoaded) return;
      this.loadingStatuses = true;
      try {
        const statuses = await getLookupDataByType("TaskStatus");

        const uniqueById = new Map();
        (statuses || []).forEach((item) => {
          if (item.isActive !== false && !uniqueById.has(item.lookupDataId)) {
            uniqueById.set(item.lookupDataId, item);
          }
        });

        const mapped = Array.from(uniqueById.values()).map((item, index) => ({
          id: `status-${item.lookupDataId}-${index}`,
          title: item.name,
          // Use Value field if present, else lookupDataId — matches Setups_Status_Task.StatusId
          value: item.value ? parseInt(item.value, 10) : item.lookupDataId,
          colour: item.colour,
        }));

        const seen = new Set();
        this.statusOptions = mapped
          .filter((item) => {
            if (seen.has(item.value)) return false;
            seen.add(item.value);
            return true;
          })
          .sort((a, b) => (a.value || 0) - (b.value || 0));
        this.statusesLoaded = true;
        // Note: intentionally NOT auto-selecting a status on create — the field
        // starts blank (like Priority) and is required, so the user must choose.
      } catch (error) {
        console.error("Error loading status options:", error);
        this.statusOptions = [];
      } finally {
        this.loadingStatuses = false;
      }
    },
    async loadPriorityOptions() {
      if (this.loadingPriorities || this.prioritiesLoaded) return; // Prevent multiple calls
      this.loadingPriorities = true;
      try {
        const priorities = await getLookupDataByType("TaskPriority");
        
        // First deduplicate by lookupDataId to remove any duplicate API responses
        const uniqueById = new Map();
        (priorities || []).forEach((item) => {
          if (item.isActive !== false && !uniqueById.has(item.lookupDataId)) {
            uniqueById.set(item.lookupDataId, item);
          }
        });
        
        // Map to our format with unique key
        const mapped = Array.from(uniqueById.values()).map((item, index) => ({
          id: `priority-${item.lookupDataId}-${index}`, // Unique identifier
          title: item.name,
          value: item.value ? parseInt(item.value, 10) : item.lookupDataId, // Use Value field if available, fallback to lookupDataId
          color: this.getColorFromHex(item.colour) || "grey",
          colour: item.colour,
        }));
        
        // Then deduplicate by value (in case multiple items have same value)
        const seen = new Set();
        const finalOptions = mapped
          .filter((item) => {
            if (seen.has(item.value)) {
              return false;
            }
            seen.add(item.value);
            return true;
          })
          .sort((a, b) => (a.value || 0) - (b.value || 0));
        
        // Set the array directly (don't mutate, replace)
        this.priorityOptions = finalOptions;
        this.prioritiesLoaded = true;
      } catch (error) {
        console.error("Error loading priority options:", error);
        // Fallback to default options if lookup fails
        this.priorityOptions = [
          { id: "priority-1", title: "Low", value: 1, color: "success", colour: "#4CAF50" },
          { id: "priority-2", title: "Medium", value: 2, color: "warning", colour: "#FF9800" },
          { id: "priority-3", title: "High", value: 3, color: "error", colour: "#F44336" },
        ];
      } finally {
        this.loadingPriorities = false;
      }
    },
    async loadAssignmentTemplates() {
      if (this.loadingTemplates || this.templatesLoaded) return;
      this.loadingTemplates = true;
      try {
        const list = await emailTemplateApi.getByType("TaskAssignment");
        // Only offer active templates; default first, then by name.
        this.assignmentTemplates = (list || [])
          .filter((t) => t.isActive !== false)
          .sort((a, b) => (b.isDefault === true) - (a.isDefault === true)
            || String(a.name).localeCompare(String(b.name)));
        this.templatesLoaded = true;
        // Preselect the default template on a fresh (create) form so the user
        // sees which email will go out, while staying free to change it.
        if (!this.isEdit && !this.localTask.emailTemplateId && this.assignmentTemplates.length) {
          const preferred = this.assignmentTemplates.find((t) => t.isDefault) || this.assignmentTemplates[0];
          if (preferred) this.onField("emailTemplateId", preferred.templateId);
        }
      } catch (error) {
        console.warn("Failed to load assignment email templates:", error);
        this.assignmentTemplates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },
    getColorFromHex(hex) {
      if (!hex) return null;
      // Map common hex colors to Vuetify color names
      const colorMap = {
        "#4CAF50": "success",
        "#8BC34A": "success",
        "#FF9800": "warning",
        "#FFC107": "warning",
        "#F44336": "error",
        "#E91E63": "error",
        "#2196F3": "primary",
        "#9C27B0": "purple",
        "#9E9E9E": "grey",
      };
      return colorMap[hex.toUpperCase()] || null;
    },
    normalizeForApi() {
      const t = { ...this.localTask };
      if (t.estimatedCost === "") t.estimatedCost = null;
      if (t.id && !t.taskId) t.taskId = t.id;
      return t;
    },
    async captureLocation() {
      if (!navigator.geolocation) {
        this.$toast?.error?.("Geolocation is not supported by your browser");
        return;
      }
      this.capturingLocation = true;
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          });
        });
        this.onField("latitude", pos.coords.latitude);
        this.onField("longitude", pos.coords.longitude);
        this.onField("accuracy", pos.coords.accuracy ?? null);
        this.$toast?.success?.("Location captured");
      } catch (err) {
        this.$toast?.error?.(err.message || "Could not get location");
      } finally {
        this.capturingLocation = false;
      }
    },
    async onSave() {
      const res = await this.$refs.form.validate();
      if (!res?.valid) return;

      this.saving = true;
      try {
        const auth = useAuthStore();
        if (!this.isEdit) {
          // set once on create
          this.localTask = { ...this.localTask, assignedFrom: auth.user?.profile?.sub || null };
        }

        const payload = this.normalizeForApi();
        console.log("Saving task with payload:", payload);
        const saved = this.isEdit
          ? await updateTask(payload)
          : await createTask(payload);

        // Upload photos on create (after task exists) - don't fail task save if upload fails
        const snaps = this.newPhotoSnapshots;
        const taskId = saved?.taskId ?? this.localTask?.taskId ?? this.localTask?.id;
        if (taskId && snaps.length > 0) {
          try {
            await Promise.all(
              snaps.map((snap) =>
                uploadPhoto({
                  Blob: snap.blob,
                  PhotoUrlId: crypto.randomUUID(),
                  TaskId: taskId,
                  CreatedAt: new Date().toISOString(),
                })
              )
            );
            revokePhotoSnapshots(this.newPhotoSnapshots);
            this.newPhotoSnapshots = [];
          } catch (photoErr) {
            console.warn("Photo upload failed (task was saved):", photoErr);
            this.$toast?.warning?.(
              "Task was created, but one or more photos failed to upload. You can retry from the edit screen."
            );
          }
        }

        this.$emit("saved", saved);

        // Clear inputs and error state so “Required” doesn’t linger
        this.$refs.form.reset(); // clears v-models
        this.$refs.form.resetValidation(); // clears error messages
        this.localTask = this.withDefaults({}); // ensure clean draft
        revokePhotoSnapshots(this.newPhotoSnapshots);
        this.newPhotoSnapshots = [];
      } catch (err) {
        console.error("Error saving task:", err);
        console.error("Error response:", err.response?.data);
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to save task";
        const errorDetails = err.response?.data?.details ? `\nDetails: ${err.response.data.details}` : "";
        this.$toast?.error?.(errorMessage + errorDetails);
      } finally {
        this.saving = false;
      }
    },
    async onResendEmail() {
      if (!this.resendTaskId) return;
      this.resending = true;
      try {
        const result = await resendTaskAssignmentEmail(this.resendTaskId);
        this.$toast?.success?.(result?.message || "Assignment email sent.");
      } catch (err) {
        const data = err.response?.data;
        const msg = data?.message || data?.error || err.message || "Failed to send email.";
        this.$toast?.error?.(msg);
      } finally {
        this.resending = false;
      }
    },
    getUserIconUrl,
  },
};
</script>
